import styled, { css } from 'styled-components';
import React, { useRef, useState } from 'react';
import useSWR from 'swr';

import { fetcher } from '../utils';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { LoadingIndicator } from '../ProjectVisualization/other-components';

const WageList = ({ month, sheetId, batchNumber, updateBatchNumber }) => {

    const { register, handleSubmit, reset } = useForm();
    const [ loading, setLoading ] = useState(false);
    const [ errorMsg, setErrorMsg ] = useState(null);
    const wagedListRef = useRef();

    const { data: wagedList, isLoading, isValidating, mutate } = useSWR( `/api/wage-processor/waged-list?month=${month}&rnd=${batchNumber}`, fetcher, {
        revalidateOnReconnect: false, revalidateOnFocus: false, keepPreviousData: true
    });

    const { data: sheetsName } = useSWR( `/api/wage-processor/sheets-names`, fetcher, {
        revalidateOnReconnect: false, revalidateOnFocus: false
    });

    async function onWagedListSubmit(formData) {
        if (!formData.wage_uuids) return setErrorMsg('請選擇要批次處理的項目');

        setLoading(true);
        const { data } = await axios.post(`/api/wage-processor/create-wage-reports`, formData);
        updateBatchNumber();
        setLoading(false);
        reset();
    }

    function handleListClick(e) {
        e.currentTarget.querySelector('input').click();
    }

    function handleSelectAllClick()
    {
        const checkboxes = [...wagedListRef.current.querySelectorAll('input[type="checkbox"]')];
        checkboxes.forEach(checkbox => {
            if (!checkbox.checked) checkbox.click();
        });
    }

    async function handleKickBackClick(e) {
        e.stopPropagation();
        e.preventDefault();   
        const uuid = e.currentTarget.dataset.uuid;

        if (!confirm('確定要退回結算嗎？')) return;

        setLoading(true);
        const { data } = await axios.delete(`/wage-processor/wage/${uuid}/delete`);
        updateBatchNumber();
        setLoading(false);
    }

    async function onGenerateBossReportsClick() {
        await axios.post(`/api/wage-processor/generate-boss-reports/${month}`);
        mutate();
    }

    async function handleUpdateSheetUsersListClick(e) {
        const dom = e.currentTarget;
        dom.disabled = true;
        const oldText = dom.innerText;
        dom.innerText = '更新中...';
        await axios.post(`/api/wage-processor/update-sheet-users`);

        dom.innerText = '更新完畢！';
        
        setTimeout(() => {
            dom.disabled = false;
            dom.innerText = oldText;
        }, 3000);
    }

    if (loading) {
        return <LoadingIndicator />
    }

    return (
        <StyledWageList loading={isLoading || isValidating}>

            <form onSubmit={handleSubmit(onWagedListSubmit)}>
                <h4>請選擇要批次處理的項目</h4>  
                { errorMsg && <div className="error">{errorMsg}</div> }

                <div className="group-label">
                    未產製
                    <button type='button' className="select-all-button" onClick={handleSelectAllClick}>全選</button>
                </div>
                <ul ref={wagedListRef}>
                    { wagedList?.filter(wage => !wage.report).map(wage => 
                        <li key={wage.uuid} onClick={handleListClick}>
                            <div className='name-label'>
                                <input type="checkbox" id={`check-${wage.uuid}`} value={wage.uuid} {...register('wage_uuids')} />
                                <label className="name" htmlFor={`check-${wage.uuid}`}>{ wage.user.name }</label>
                                <button data-uuid={wage.uuid} onClick={handleKickBackClick}>退回結算</button>
                            </div>
                        </li>
                    )}
                </ul>

                <div className="group-label">已產製</div>
                <ul>
                    { wagedList?.filter(wage => wage.report).map(wage => 
                        <li key={wage.uuid} onClick={handleListClick}>
                            <div className='name-label'>
                                <input type="checkbox" id={`check-${wage.uuid}`} value={wage.uuid} {...register('wage_uuids')} />
                                <label className="name" htmlFor={`check-${wage.uuid}`}>{ wage.user.name }</label>
                                <button data-uuid={wage.uuid} onClick={handleKickBackClick}>退回結算</button>
                            </div>
                            <div className="report">
                                { new Date(wage.report.updated_at).toLocaleString('zh-TW') } 產製
                            </div>
                        </li>
                    )}
                </ul>

                {/* <button type="button" onClick={onGenerateBossReportsClick}>幫志祺跟成祥產製</button> */}

                <div className='export-tools'>  

                    <div className='select-group'>
                        <label>代墊款項工作表</label>
                        <select {...register('wage_other_sheet_name')} required>
                            <option value=''>（請選擇代墊款項工作表）</option>
                            { sheetsName?.map?.(name => <option key={name} value={name}>{name}</option>) }
                        </select>
                    </div>

                    <div className='select-group'>
                        <label>薪資加項或扣項作表</label>
                        <select {...register('wage_bonuce_sheet_name')} required>
                            <option value=''>（請選擇薪資加項或扣項作表）</option>
                            { sheetsName?.map?.(name => <option key={name} value={name}>{name}</option>) }
                        </select>
                    </div>
                    
                    <div className='sheet-link'>
                        <a href={`https://docs.google.com/spreadsheets/d/${sheetId}/edit`} target="_blank">開啟批次處理 Google Sheets</a>
                        <button onClick={handleUpdateSheetUsersListClick}>更新 Google Sheets 上的員工列表</button>
                    </div>

                    <button type='submit' className='btn btn-default'>開始產製</button>
                </div>
            </form>

        </StyledWageList>
    )
}

const StyledWageList = styled.div`
    ${ p => p.loading && css`
        opacity: 0.5;
        pointer-events: none;
    `}

    .group-label {
        font-size: 14px;
        font-weight: 700;
        color: #666;
        margin-top: 40px;
        margin-bottom: 16px;
    }

    ul {
        display: grid;
        gap: 8px;
        grid-template-columns: repeat(5, 1fr);
    }

    li {
        cursor: pointer;
        border: 1px solid #ddd;
        padding: 8px 12px;

        &:hover {
            background: #fff;
        }

        &:not(:hover) .name-label button {
            display: none;
        }

        * {
            pointer-events: none;
            user-select: none;
        }

        .name-label {
            display: flex;
            align-items: center;
        }

        input {
            margin-top: 0;
            width: 20px;
            height: 20px;
        }

        label {
            margin: 0;
            margin-left: 12px;
            line-height: 20px;
            font-size: 16px;
        }

        .name-label button {
            display: block;
            margin-left: auto;
            pointer-events: auto;
            padding: 0px 8px;
            border: 1px solid #c66;
            border-radius: 2px;
            background-color: #fff;
            color: #c66;
            font-weight: 700;
        }

        .report {
            font-size: 12px;
            color: #777;
        }
    }

    .select-all-button {
        display: inline-block;
        margin-left: 64px;
    }

    .select-group {
        margin-top: 8px;
    }

    .export-tools {
        margin-top: 24px;
        padding-top: 24px;
        border-top: 1px solid #ddd;

        label {
            min-width: 160px;
        }

        .sheet-link {
            margin-top: 16px;

            button {
                margin-left: 16px;
            }
        }

        >button {
            display: block;
            margin-left: auto;
        }
    }
`

export default WageList